import { memo } from 'react';
import { RotateCw, ShieldAlert, X } from 'lucide-react';
import { Button } from '@cyphex/ui';
import type { OutboxItem } from '../../types';

interface PendingSendNoticeProps {
  pending: OutboxItem;
  onRetry: (clientMsgId: string) => void;
  /** Re-arms the send with the data-protection acknowledgement attached. */
  onSendAnyway: (clientMsgId: string) => void;
  onDiscard: (clientMsgId: string) => void;
}

/**
 * The per-message failed-send affordance.
 *
 * Shows the reason, not just the fact: "Not sent" alone leaves the user with no
 * idea whether to retry now or fix something first. Retry re-arms the outbox
 * item — the send is idempotent on `clientMsgId`, so a retry after a lost
 * RESPONSE returns the original message rather than posting a duplicate.
 *
 * A DATA-PROTECTION WARNING renders differently on purpose. It is not a
 * failure, it is a question, and "Retry" is the wrong verb for it — the
 * identical request would be refused identically. "Send anyway" appears ONLY
 * when the server said the rule is acknowledgeable; a blocked send never shows
 * it, because a button that cannot work is worse than no button at all.
 */
export const PendingSendNotice = memo(function PendingSendNotice({
  pending,
  onRetry,
  onSendAnyway,
  onDiscard,
}: PendingSendNoticeProps) {
  if (pending.state !== 'failed') return null;

  const refusal = pending.dlpRefusal;
  if (refusal !== null) {
    const rules = refusal.violations.map((violation) => violation.ruleName).join(', ');
    return (
      <div
        role="alert"
        className="mt-1.5 flex flex-wrap items-center gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/5 px-2 py-1"
      >
        <ShieldAlert size={14} className="text-yellow-700 dark:text-yellow-500" aria-hidden />
        <span className="text-sm font-medium text-foreground">This looks like sensitive data.</span>
        {rules.length > 0 ? (
          <span className="text-sm text-muted-foreground">Matched: {rules}</span>
        ) : null}
        <Button variant="link" size="xs" onClick={() => onSendAnyway(pending.clientMsgId)}>
          Send anyway
        </Button>
        <Button variant="link" size="xs" leftIcon={X} onClick={() => onDiscard(pending.clientMsgId)}>
          Discard
        </Button>
      </div>
    );
  }

  return (
    <div
      role="alert"
      className="mt-1.5 flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1"
    >
      <span className="text-sm font-medium text-destructive">
        Not sent{pending.attempts > 1 ? ` after ${pending.attempts} attempts` : ''}.
      </span>
      {pending.lastError ? (
        <span className="text-sm text-muted-foreground">{pending.lastError}</span>
      ) : null}
      <Button variant="link" size="xs" leftIcon={RotateCw} onClick={() => onRetry(pending.clientMsgId)}>
        Retry
      </Button>
      <Button variant="link" size="xs" leftIcon={X} onClick={() => onDiscard(pending.clientMsgId)}>
        Discard
      </Button>
    </div>
  );
});
